import { fxRateApi } from '@/lib/api';
import { metadataDb } from '@/lib/db';
import type { FxRate, FxConversionResult, FxRateHistory } from '@/types';

interface CachedRates {
  rates: Map<string, number>;
  timestamp: string;
}

/**
 * FX Rate service for currency conversion.
 * Caches rates locally for offline use.
 */
export const FxRateService = {
  /**
   * Get rate for a currency pair.
   */
  async getRate(from: string, to: string, date?: string): Promise<number | null> {
    // Normalize to uppercase
    const fromCurrency = from.toUpperCase();
    const toCurrency = to.toUpperCase();
    
    // Same currency
    if (fromCurrency === toCurrency) {
      return 1;
    }

    // Try cached rates first
    const cached = await this.getCachedRates();
    const cacheKey = `${fromCurrency}_${toCurrency}`;
    
    if (cached?.rates.has(cacheKey)) {
      return cached.rates.get(cacheKey) ?? null;
    }

    // Fetch from API if online
    if (navigator.onLine) {
      try {
        let rateData: FxRate;
        if (date) {
          rateData = await fxRateApi.getAtDate(fromCurrency, toCurrency, date);
        } else {
          rateData = await fxRateApi.getLatest(fromCurrency, toCurrency);
        }
        await this.cacheRate(fromCurrency, toCurrency, rateData.rate);
        return rateData.rate;
      } catch (error) {
        console.error('Failed to fetch FX rate:', error);
      }
    }

    // Try cross-rate calculation via USD
    if (fromCurrency !== 'USD' && toCurrency !== 'USD') {
      const fromUsd = await this.getRate(fromCurrency, 'USD', date);
      const usdTo = await this.getRate('USD', toCurrency, date);
      
      if (fromUsd && usdTo) {
        return fromUsd * usdTo;
      }
    }

    return null;
  },

  /**
   * Convert an amount from one currency to another.
   */
  async convert(
    amount: number,
    from: string,
    to: string,
    date?: string
  ): Promise<FxConversionResult> {
    const fromCurrency = from.toUpperCase();
    const toCurrency = to.toUpperCase();

    if (fromCurrency === toCurrency) {
      return {
        originalAmount: amount,
        originalCurrency: fromCurrency,
        convertedAmount: amount,
        targetCurrency: toCurrency,
        rate: 1,
        rateDate: date ?? new Date().toISOString().split('T')[0],
        isApproximate: false,
      };
    }

    // Try API first for accurate conversion
    if (navigator.onLine) {
      try {
        const response = await fxRateApi.convert(amount, fromCurrency, toCurrency, date);
        return {
          originalAmount: response.originalAmount,
          originalCurrency: response.fromCurrency,
          convertedAmount: response.convertedAmount,
          targetCurrency: response.toCurrency,
          rate: response.rateUsed,
          rateDate: response.rateDate,
          isApproximate: false,
        };
      } catch (error) {
        console.error('Failed to convert via API:', error);
      }
    }

    // Fallback to cached rates
    const rate = await this.getRate(fromCurrency, toCurrency, date);
    
    if (rate === null) {
      throw new Error(`Unable to convert from ${fromCurrency} to ${toCurrency}`);
    }

    return {
      originalAmount: amount,
      originalCurrency: fromCurrency,
      convertedAmount: amount * rate,
      targetCurrency: toCurrency,
      rate,
      rateDate: date ?? new Date().toISOString().split('T')[0],
      isApproximate: true,
    };
  },

  /**
   * Get historical rates for charting.
   */
  async getHistory(
    from: string,
    to: string,
    startDate: string,
    endDate: string
  ): Promise<FxRateHistory[]> {
    if (!navigator.onLine) {
      throw new Error('Rate history requires an internet connection');
    }

    const history = await fxRateApi.getHistory(
      from.toUpperCase(),
      to.toUpperCase(),
      startDate,
      endDate
    );

    return [history];
  },

  /**
   * Get cached rates from IndexedDB.
   */
  async getCachedRates(): Promise<CachedRates | null> {
    const cached = await metadataDb.get('fx_rates');
    if (!cached) return null;

    try {
      const data = JSON.parse(cached);
      const rates = new Map(Object.entries(data.rates) as [string, number][]);
      
      return {
        rates,
        timestamp: data.timestamp,
      };
    } catch {
      return null;
    }
  },

  /**
   * Cache a rate.
   */
  async cacheRate(from: string, to: string, rate: number): Promise<void> {
    const cached = await this.getCachedRates();
    const rates = cached?.rates ?? new Map<string, number>();
    
    const key = `${from}_${to}`;
    rates.set(key, rate);
    
    // Also cache inverse
    if (rate > 0) {
      rates.set(`${to}_${from}`, 1 / rate);
    }

    await metadataDb.set('fx_rates', JSON.stringify({
      rates: Object.fromEntries(rates),
      timestamp: new Date().toISOString(),
    }));
  },

  /**
   * Check if cached rates are stale.
   */
  async isRateCacheStale(): Promise<boolean> {
    const cached = await this.getCachedRates();
    if (!cached) return true;

    const cacheTime = new Date(cached.timestamp);
    const now = new Date();
    
    // Consider stale after 12 hours
    const twelveHours = 12 * 60 * 60 * 1000;
    return (now.getTime() - cacheTime.getTime()) > twelveHours;
  },

  /**
   * Refresh rate cache.
   */
  async refreshCache(currencies: string[]): Promise<void> {
    if (!navigator.onLine) return;

    for (const from of currencies) {
      for (const to of currencies) {
        if (from !== to) {
          try {
            const rateData = await fxRateApi.getLatest(from.toUpperCase(), to.toUpperCase());
            await this.cacheRate(from.toUpperCase(), to.toUpperCase(), rateData.rate);
          } catch (error) {
            console.error(`Failed to cache rate ${from}/${to}:`, error);
          }
        }
      }
    }
  },

  /**
   * Format currency amount for display.
   */
  formatAmount(amount: number, currency: string, locale = 'en-US'): string {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);
  },

  /**
   * Format rate for display.
   */
  formatRate(rate: number, decimals = 4): string {
    return rate.toFixed(decimals);
  },

  /**
   * Get common currencies.
   */
  getCommonCurrencies(): string[] {
    return [
      'USD', 'EUR', 'COP', 'MXN'
    ];
  },

  /**
   * Get currency symbol.
   */
  getCurrencySymbol(currency: string): string {
    const symbols: Record<string, string> = {
      USD: '$',
      EUR: '€',
      GBP: '£',
      JPY: '¥',
      CHF: 'Fr',
      CAD: 'C$',
      AUD: 'A$',
      CNY: '¥',
      HKD: 'HK$',
      SGD: 'S$',
      NZD: 'NZ$',
      KRW: '₩',
      SEK: 'kr',
      NOK: 'kr',
      MXN: '$',
      BRL: 'R$',
      INR: '₹',
      ZAR: 'R',
      TRY: '₺',
      RUB: '₽',
    };
    
    return symbols[currency.toUpperCase()] ?? currency;
  },
};
